/**
 * Copyright IBM Corp. 2022, 2026
 */

import { type ForwardRefExoticComponent, forwardRef } from 'react'
import type { ButtonProps } from './types'
import { determineColor, LINK_TYPE_ICON_MAP } from './utils'
import { StandaloneLink } from '../standalone-link'
import { Button } from '../button'

/** @deprecated Use <Button> from '@hashicorp/mds-react/button' instead. Will be removed in v1.0. */
const MDSButton = forwardRef<
	HTMLAnchorElement | HTMLButtonElement,
	ButtonProps
>(
	(
		{
			locale,
			text,
			title,
			url,
			href,
			linkType,
			onClick,
			external,
			className,
			size,
			icon,
			iconPosition = 'trailing',
			label,
			disabled,
			type,
			theme,
			...rest
		},
		ref,
	) => {
		const color = determineColor(theme)

		if (color === 'tertiary') {
			// We prefer the Standalone Link component over the tertiary
			// button styles.
			return (
				<StandaloneLink
					text={(text || title) as string}
					color={
						theme?.background === 'dark' ? 'secondary-inverted' : 'secondary'
					}
					href={url || href}
					icon={linkType ? LINK_TYPE_ICON_MAP[linkType] : icon}
					iconPosition={linkType ? 'trailing' : iconPosition}
					className={className}
					onClick={onClick as () => void}
					size={size}
					isHrefExternal={linkType === 'outbound' || external}
					ref={ref as ForwardRefExoticComponent<HTMLAnchorElement>}
					locale={locale}
				/>
			)
		}

		return (
			<Button
				text={text || title}
				href={url || href}
				isHrefExternal={linkType === 'outbound' || external}
				color={determineColor(theme)}
				type={type}
				icon={linkType ? LINK_TYPE_ICON_MAP[linkType] : icon}
				iconPosition={linkType ? 'trailing' : iconPosition}
				onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
				className={className}
				// Default buttons to the large size unless we specifically request the
				// small variant
				size={size === 'small' ? 'medium' : 'large'}
				aria-label={label}
				disabled={disabled}
				ref={ref as ForwardRefExoticComponent<HTMLButtonElement>}
				locale={locale}
				{...rest}
			/>
		)
	},
)

MDSButton.displayName = 'MDSButton'

export { MDSButton }
